import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faAward, faCalendarCheck, faStar, faUtensils, 
  faChartLine, faBell, faMobile, faUsers,
  faArrowRight, faCheck, faEnvelope
} from '@fortawesome/free-solid-svg-icons';
import '../styles/LandingPage.css';
import EnquiryForm from './EnquiryForm';
import { trackLead, trackDemoRequest, trackEvent } from '../utils/analytics';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('loyalty');
  const [isVisible, setIsVisible] = useState({});
  const navigate = useNavigate();
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);

    // Create structured data for SEO
  useEffect(() => {
    // Organization schema
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "My Local",
      "url": "https://my-local.co.za",
      "logo": "https://my-local.co.za/images/mylocal1.png",
      "description": "Comprehensive digital solutions for local businesses with loyalty programs, booking systems, specials and menu management, review collection and digital marketing tools."
    };

    // Software application schema
    const softwareSchema = {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "My Local Business Platform",
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web, iOS, Android",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "ZAR"
      }
    };

    // Add schemas to the page
    const addJSONLD = (schema) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
      
      return () => {
        document.head.removeChild(script);
      };
    };

    // Add both schemas
    const cleanup1 = addJSONLD(organizationSchema);
    const cleanup2 = addJSONLD(softwareSchema);

    // Cleanup when component unmounts
    return () => {
      cleanup1();
      cleanup2();
    };
  }, []);

  // Event tracking functions
  const handleSignUpClick = () => {
    trackEvent('sign_up_click', { page: 'landing' });
    trackLead('Get Started Click', { source: 'landing_page' }); 
    navigate('/vendor/register');
  };

  const handleLoginClick = () => {
    trackEvent('login_click', { page: 'landing' });
    navigate('/login');
  };

  const handleContactClick = () => {
    trackEvent('contact_form_open', { page: 'landing' });
    trackDemoRequest('landing_page_contact'); 
    setShowEnquiryForm(true);
  };

  const handleDemoClick = () => {
    setShowDemoModal(true);
    trackEvent('demo_requested', {
      page: 'landing',
      source: 'demo_button'
    });
    trackDemoRequest('landing_page_cta');
  };

  // Handler to close the enquiry form
  const handleCloseEnquiryForm = () => {
    setShowEnquiryForm(false);
  };

  // Handle scroll animations
  useEffect(() => {
    const handleScroll = () => {
      const sections = document.querySelectorAll('.animate-on-scroll');
      
      sections.forEach(section => {
        const sectionTop = section.getBoundingClientRect().top;
        const sectionId = section.id;
        
        if (sectionTop < window.innerHeight * 0.75) {
          setIsVisible(prev => ({ ...prev, [sectionId]: true }));
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on initial load
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    trackEvent('feature_tab_click', { 
      tab_name: tab, 
      feature: features[tab].title 
    });
  };


  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const plans = [
    {
      name: "Free",
      price: "R0",
      period: "forever",
      features: [
        "Up to 25 customers",
        "Loyalty Program Management",
        "Basic Analytics",
        "Mobile Optimized"
      ],
      buttonText: "Get Started",
      buttonAction: () => navigate('/vendor/register')
    },
    {
  name: "Premium",
  // price: <><s>R1800</s> R600</>,
  price: "R1800",
  period: "per month",
  features: [
    "Unlimited customers",
    "Advanced analytics",
    "Email campaigns",
    "Push notifications",
    "Booking system",
    "Menu management",
    "Customer reviews",
    "Priority support"
  ],
  highlighted: true,
  specialTag: "Limited Time Offer",
  buttonText: "Sign Up Now",
  buttonAction: () => navigate('/vendor/register')
},
    {
      name: "Enterprise",
      price: "Custom",
      period: "tailored for you",
      features: [
        "All Premium features",
        "Custom integrations",
        "Dedicated account manager",
        "White-label solution",
        "Advanced API access"
      ],
      buttonText: "Contact Us",
       buttonAction: () => setShowEnquiryForm(true)
    }
  ];

  const features = {
    loyalty: {
      title: "Loyalty Programs",
      description: "Build lasting customer relationships with customizable loyalty programs that keep your customers coming back.",
      icon: faAward,
      image: "https://images.unsplash.com/photo-1556742393-d75f468bfcb0?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      points: [
        "Digital stamp cards",
        "Automatic rewards",
        "Customer management",
        "Performance analytics"
      ]
    },
    bookings: {
      title: "Booking System",
      description: "Streamline your reservation process with our easy-to-use booking system, saving time for both you and your customers.",
      icon: faCalendarCheck,
      image: "https://images.unsplash.com/photo-1517502884422-41eaead166d4?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      points: [
        // "Real-time availability",
        "Email confirmations",
        // "Calendar management",
        "Customer notifications"
      ]
    },
    reviews: {
      title: "Customer Reviews",
      description: "Gather valuable feedback and showcase your reputation with our integrated review platform.",
      icon: faStar,
      image: "https://images.unsplash.com/photo-1511467687858-23d96c32e4ae?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      points: [
        "Star ratings",
        "Customer testimonials",
        "Review management",
        "Response tools"
      ]
    },
    marketing: {
      title: "Digital Menu & Specials",
      description: "Showcase your offerings and promote special deals with beautifully designed digital menus.",
      icon: faUtensils,
      image: "https://images.unsplash.com/photo-1515669097368-22e68427d265?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80",
      points: [
        "Digital menu creation",
        "Special promotions",
        // "Menu analytics",
        // "QR code generation"
      ]
    }
  };

  const testimonials = [
    {
      text: "We've seen a 30% increase in repeat customers since implementing My Local's loyalty program. The digital system is so much easier than paper cards!",
      author: "Sarah Johnson",
      business: "Coffee Connection",
      avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80"
    },
    {
      text: "The booking system has revolutionized how we manage our reservations. No more double bookings or missed appointments!",
      author: "Michael Chen",
      business: "Sunset Restaurant",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80"
    },
    {
      text: "Our customers love being able to collect rewards digitally. The push notifications feature has been a game-changer for our promotions.",
      author: "Emma Williams",
      business: "Organic Market",
      avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80"
    }
  ];

  return (
    <div className="landing-page">
       {/* Render the EnquiryForm when showEnquiryForm is true */}
      {showEnquiryForm && <EnquiryForm onClose={handleCloseEnquiryForm} />}

      {/* Header */}
      <header className="header">
        <div className="container">
     <div className="logo">
      <img 
        src="/images/logo-mylocal.png" 
        alt="My Local logo" 
        width="250" 
        height="auto" 
        className="logo-img"
      />
    </div>
          <nav className={`nav ${isMenuOpen ? 'active' : ''}`}>
            <ul className="nav-list">
              <li><a href="#features-section" onClick={() => setIsMenuOpen(false)}>Features</a></li>
              <li><a href="#pricing" onClick={() => setIsMenuOpen(false)}>Pricing</a></li>
              {/* <li><a href="#testimonials" onClick={() => setIsMenuOpen(false)}>Testimonials</a></li> */}
             <li><a href="#" onClick={(e) => { e.preventDefault(); handleContactClick(); setIsMenuOpen(false); }}>Contact</a></li>
            </ul>
            <div className="nav-buttons">
              <button className="btn btn-outline" onClick={handleLoginClick}>Log In</button>
              <button className="btn btn-primary" onClick={handleSignUpClick}>Sign Up</button>
            </div>
          </nav>
          <div className="menu-toggle" onClick={toggleMenu}>
            <div className={`hamburger ${isMenuOpen ? 'active' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <h1>The Complete Digital Solution for Local Businesses</h1>
            <p>Boost customer loyalty, streamline bookings, showcase menus, and collect reviews – all in one platform.</p>
            <div className="hero-buttons">
                 <button 
                className="btn btn-primary" 
                onClick={() => {
                  trackEvent('hero_cta_click', {
                    button_text: 'Get Started'
                  });
                  navigate('/vendor/register');
                }}
              >
                Get Started <FontAwesomeIcon icon={faArrowRight} />
              </button>
              <button 
                className="btn btn-outline" 
                onClick={() => {
                  trackEvent('learn_more_click', {});
                  document.getElementById('features-section').scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Learn More
              </button>
            </div>
          </div>
          <div className="hero-image">
            <img src="https://images.unsplash.com/photo-1556740738-b6a63e27c4df?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80" alt="Digital business solutions" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {/* <section className="stats">
        <div className="container">
          <div className="stats-item">
            <h3>500+</h3>
            <p>Businesses</p>
          </div>
          <div className="stats-item">
            <h3>50,000+</h3>
            <p>Loyal Customers</p>
          </div>
          <div className="stats-item">
            <h3>98%</h3>
            <p>Customer Satisfaction</p>
          </div>
          <div className="stats-item">
            <h3>30%</h3>
            <p>Avg. Revenue Increase</p>
          </div>
        </div>
      </section> */}

      <section className="stats" aria-labelledby="stats-heading">
        <h2 id="stats-heading" className="visually-hidden">Business Impact Statistics</h2>
        <div className="container">
          <div className="stats-item">
            <h3>71%</h3>
            <p>Higher Recommendation Rate</p>
          </div>
          <div className="stats-item">
            <h3>58%</h3>
            <p>Reduction in No-Shows</p>
          </div>
          <div className="stats-item">
            <h3>67%</h3>
            <p>More Spent by Loyal Customers</p>
          </div>
          <div className="stats-item">
            <h3>18%</h3>
            <p>Sales Increase from Reviews</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features-section" className={`features animate-on-scroll ${isVisible['features-section'] ? 'visible' : ''}`}>
        <div className="container">
          <div className="section-header">
            <h2>Comprehensive Business Solutions</h2>
            <p>Everything you need to grow your business and delight your customers</p>
          </div>

          <div className="feature-tabs">
            <div className="feature-tab-buttons">
              {Object.keys(features).map(key => (
                <button
                  key={key}
                  className={`tab-button ${activeTab === key ? 'active' : ''}`}
                  onClick={() => handleTabClick(key)}
                >
                  <FontAwesomeIcon icon={features[key].icon} />
                  <span>{features[key].title}</span>
                </button>
              ))}
            </div>

            <div className="feature-tab-content">
              <div className="feature-info">
                <h3>{features[activeTab].title}</h3>
                <p>{features[activeTab].description}</p>
                <ul className="feature-list">
                  {features[activeTab].points.map((point, index) => (
                    <li key={index}>
                      <FontAwesomeIcon icon={faCheck} className="check-icon" />
                      {point}
                    </li>
                  ))}
                </ul>
              <button 
              className="btn btn-primary" 
              onClick={() => {
                trackEvent('feature_cta_click', {
                  feature: features[activeTab].title
                });
                navigate('/vendor/register');
              }}
            >
              Get Started <FontAwesomeIcon icon={faArrowRight} />
            </button>
              </div>
              <div className="feature-image">
                <img 
                  src={features[activeTab].image} 
                  alt={features[activeTab].title} 
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits-section" className={`features animate-on-scroll ${isVisible['features-section'] ? 'visible' : ''}`}>
        <div className="container">
          <div className="section-header">
            <h2>Why Choose My Local?</h2>
            <p>Elevate your business with our all-in-one digital platform</p>
          </div>

          <div className="benefits-grid">
            <div className={`benefit-card ${isVisible['benefits-section'] ? 'visible' : ''}`}>
              <div className="benefit-icon">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <h3>Increase Revenue</h3>
              <p>Boost customer retention and spending with targeted loyalty programs and promotions.</p>
            </div>

            <div className={`benefit-card ${isVisible['benefits-section'] ? 'visible' : ''}`}>
              <div className="benefit-icon">
                <FontAwesomeIcon icon={faBell} />
              </div>
              <h3>Engage Customers</h3>
              <p>Keep your audience informed with push notifications and email campaigns.</p>
            </div>

            <div className={`benefit-card ${isVisible['benefits-section'] ? 'visible' : ''}`}>
              <div className="benefit-icon">
                <FontAwesomeIcon icon={faMobile} />
              </div>
              <h3>Mobile Optimized</h3>
              <p>Provide a seamless experience on any device with our responsive platform.</p>
            </div>

            <div className={`benefit-card ${isVisible['benefits-section'] ? 'visible' : ''}`}>
              <div className="benefit-icon">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <h3>Customer Insights</h3>
              <p>Gain valuable data on customer behavior and preferences to inform your strategy.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className={`features animate-on-scroll ${isVisible['features-section'] ? 'visible' : ''}`}>
        <div className="container">
          <div className="section-header">
            <h2>Simple, Transparent Pricing</h2>
            <p>Choose the plan that works for your business</p>
          </div>

        <div className="pricing-grid">
          {plans.map((plan, index) => (
            <div 
              key={index} 
              className={`pricing-card ${plan.highlighted ? 'highlighted' : ''} ${isVisible['pricing'] ? 'visible' : ''}`}
              style={{animationDelay: `${index * 0.2}s`}}
            >
              {plan.highlighted && <div className="popular-tag">Most Popular</div>}
              {plan.specialTag && <div className="special-tag">{plan.specialTag}</div>}
              <h3 className="plan-name">{plan.name}</h3>
              <div className="plan-price">
                <span className="price">{plan.price}</span>
                <span className="period">{plan.period}</span>
              </div>
              <ul className="plan-features">
                {plan.features.map((feature, idx) => (
                  <li key={idx}>
                    <FontAwesomeIcon icon={faCheck} className="feature-check" />
                    {feature}
                  </li>
                ))}
              </ul>
              <button 
                className={`btn ${plan.highlighted ? 'btn-primary' : 'btn-outline'}`}
                onClick={plan.buttonAction}
              >
                {plan.buttonText}
              </button>
            </div>
          ))}
        </div>
        </div>
      </section>

      {/* Testimonials */}
      {/* <section id="testimonials" className={`features animate-on-scroll ${isVisible['features-section'] ? 'visible' : ''}`}>
        <div className="container">
          <div className="section-header">
            <h2>What Our Customers Say</h2>
            <p>Success stories from businesses using My Local</p>
          </div>

          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <div 
                key={index} 
                className={`testimonial-card ${isVisible['testimonials'] ? 'visible' : ''}`}
                style={{animationDelay: `${index * 0.2}s`}}
              >
                <div className="testimonial-content">
                  <p>"{testimonial.text}"</p>
                </div>
                <div className="testimonial-author">
                  <img src={testimonial.avatar} alt={testimonial.author} className="author-avatar" />
                  <div className="author-info">
                    <h4>{testimonial.author}</h4>
                    <p>{testimonial.business}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      {/* <section className={`features animate-on-scroll ${isVisible['features-section'] ? 'visible' : ''}`}>
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Transform Your Business?</h2>
            <p>Join thousands of businesses already growing with My Local</p>
            <div className="cta-form">
              <div className="form-group">
                <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                <input type="email" placeholder="Enter your email" />
              </div>
              <button className="btn btn-primary" style={{ width: '10em', height: '50px' }}>Get Started</button>
            </div>
            <p className="cta-note">Free to try. No credit card required.</p>
          </div>
        </div>
      </section> */}

        {/* Demo CTA Section */}
        <section className={`features animate-on-scroll ${isVisible['features-section'] ? 'visible' : ''}`}>
          <div className="container">
            <div className="cta-content">
              <h2>See My Local in Action</h2>
              <p>Book a personalized demo to discover how our platform can transform your business</p>
                  <button 
              className="btn btn-primary" 
              onClick={handleDemoClick}  // You already have this function defined above
              style={{ padding: '12px 24px', fontSize: '16px' }}
            >
              <FontAwesomeIcon icon={faCalendarCheck} style={{ marginRight: '8px' }} />
              Schedule a Demo
            </button>
              <p className="cta-note">Quick, easy, and personalized to your business needs</p>
            </div>
          </div>
        </section>

        {/* Demo Modal */}
        {showDemoModal && (
          <div className="demo-modal-overlay" onClick={() => setShowDemoModal(false)}>
            <div className="demo-modal-content" onClick={e => e.stopPropagation()}>
              <button className="close-demo-modal-btn" onClick={() => setShowDemoModal(false)}>×</button>
              <h3>Book Your Personalized Demo</h3>
              <div className="calendar-container">
                <iframe
                  src="https://cal.com/duanedp/my-local-product-walk-through-demo?embed=true"
                  width="100%"
                  height="600px"
                  frameBorder="0"
                  title="Schedule a demo"
                ></iframe>
              </div>
            </div>
          </div>
        )}

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-col">
              <div className="footer-logo">
                <span className="logo-text">My Local</span>
              </div>
              <p>Comprehensive digital solutions for local businesses to grow and thrive.</p>
              {/* <div className="social-icons">
                <a href="#" className="social-icon"><i className="fab fa-facebook-f"></i></a>
                <a href="#" className="social-icon"><i className="fab fa-twitter"></i></a>
                <a href="#" className="social-icon"><i className="fab fa-instagram"></i></a>
                <a href="#" className="social-icon"><i className="fab fa-linkedin-in"></i></a>
              </div> */}
            </div>
            <div className="footer-col">
              {/* <h4>Company</h4>
              <ul>
                <li><a href="#">About Us</a></li>
                <li><a href="#">Careers</a></li>
                <li><a href="#">Blog</a></li>
                <li><a href="#">Press</a></li>
              </ul> */}
            </div>
            <div className="footer-col">
              <h4>Solutions</h4>
              <ul>
                <li><a href="#features-section">Loyalty Programs</a></li>
                <li><a href="#features-section">Booking System</a></li>
                <li><a href="#features-section">Customer Reviews</a></li>
                <li><a href="#features-section">Digital Menus</a></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Support</h4>
              <ul>
                {/* <li><a href="#">Help Center</a></li> */}
               <li><a href="#" onClick={(e) => { e.preventDefault(); handleContactClick(); }}>Contact Us</a></li>
                {/* <li><a href="#">Privacy Policy</a></li>
                <li><a href="#">Terms of Service</a></li> */}
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} My Local. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;